import {
  DEFAULT_PRACTICE_SETTINGS,
  DEFAULT_STYLING_SETTINGS,
  DEFAULT_WINDOW_STATE,
  deserializePinHash,
  hashPin,
  isDefaultPin,
  isHashedPin,
  serializePinHash,
  verifyPinHash,
  type PlatformAdapter,
  type KioskEmergencyCacheSnapshot,
  type PracticeSettings,
  type StylingSettings,
} from "@ascleo/frontdesk-shared";
import {
  decodeKioskEmergencyCacheJson,
  encodeKioskEmergencyCacheSnapshot,
} from "@ascleo/frontdesk-shared/kiosk-policy/cache";

export const DEMO_TERMINAL_ID = "demo-terminal";

const practiceSettingsKey = "frontdesk-web-practice-settings";
const stylingSettingsKey = "frontdesk-web-styling-settings";
const kioskEmergencyCacheKey = "frontdesk-kiosk-emergency-cache";
const placeholderPin = "••••";

const demoPracticeSettings: PracticeSettings = {
  ...DEFAULT_PRACTICE_SETTINGS,
  bridgeUrl: "",
  practiceName: "Ascleo Demo Praxis",
  welcomeMessage: "",
  selectedTerminalId: DEMO_TERMINAL_ID,
  deviceName: "Browser Demo",
  schedulerAvailableInSelfService: true,
  prescriptionServiceAvailableInSelfService: true,
  prescriptionRequestQuarterlyCheck: "warn",
  prescriptionRequestEstimatedTime: "Heute bis 16:00 Uhr",
};

const demoStylingSettings: StylingSettings = {
  ...DEFAULT_STYLING_SETTINGS,
  pin: "",
  language: "de",
  defaultKioskLanguage: "de",
  voiceOverVolume: 45,
};

/**
 * Creates a browser-only platform adapter backed by localStorage for non-secrets.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const adapter = createBrowserPlatformAdapter()
 * ```
 */
export function createBrowserPlatformAdapter(): PlatformAdapter {
  return {
    getPracticeSettings,
    setPracticeSettings,
    getBridgeUrl,
    setBridgeUrl,
    getKioskEmergencyCache,
    setKioskEmergencyCache,
    clearKioskEmergencyCache,
    getStylingSettings,
    setStylingSettings,
    verifyPin,
    isPinConfigured,
    setFullscreen: async () => undefined,
    exitFullscreen: async () => undefined,
    enterKioskMode: async () => undefined,
    getWindowState: async () => DEFAULT_WINDOW_STATE,
    toggleDevTools: async () => undefined,
    getSystemVolume: async () => 50,
    setSystemVolume: async () => true,
    getMuted: async () => false,
    setMuted: async () => true,
    onRequestExitFullscreen: () => () => undefined,
  };
}

export function createDemoPlatformAdapter(): PlatformAdapter {
  return {
    getPracticeSettings: async () => ({
      ...demoPracticeSettings,
      bridgeUrl: getSameOriginGatewayUrl("demo"),
      solToken: undefined,
    }),
    setPracticeSettings: async () => undefined,
    getBridgeUrl: async () => getSameOriginGatewayUrl("demo"),
    setBridgeUrl: async () => undefined,
    getKioskEmergencyCache: async () => undefined,
    setKioskEmergencyCache: async () => undefined,
    clearKioskEmergencyCache: async () => undefined,
    getStylingSettings: async () => demoStylingSettings,
    setStylingSettings: async () => undefined,
    verifyPin: async () => false,
    // The demo never exposes settings, so it is neither unlockable nor in need
    // of forced PIN setup.
    isPinConfigured: async () => true,
    setFullscreen: async () => undefined,
    exitFullscreen: async () => undefined,
    enterKioskMode: async () => undefined,
    getWindowState: async () => DEFAULT_WINDOW_STATE,
    toggleDevTools: async () => undefined,
    getSystemVolume: async () => 50,
    setSystemVolume: async () => true,
    getMuted: async () => false,
    setMuted: async () => true,
    onRequestExitFullscreen: () => () => undefined,
  };
}

/**
 * Returns the same-origin gateway base URL for shared RPC clients.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const url = getSameOriginGatewayUrl()
 * ```
 */
export function getSameOriginGatewayUrl(
  mode: "live" | "demo" = "live",
): string {
  const path = mode === "demo" ? "/api/frontdesk/demo" : "/api/frontdesk";
  return new URL(path, window.location.origin).toString();
}

async function getPracticeSettings(): Promise<PracticeSettings> {
  const stored = readStoredRecord(practiceSettingsKey);
  return {
    ...DEFAULT_PRACTICE_SETTINGS,
    ...stored,
    bridgeUrl: getSameOriginGatewayUrl(),
    solToken: undefined,
  };
}

async function setPracticeSettings(
  settings: Partial<PracticeSettings>,
): Promise<void> {
  const current = await getPracticeSettings();
  const merged = stripSecrets({
    ...current,
    ...settings,
    bridgeUrl: getSameOriginGatewayUrl(),
  });
  localStorage.setItem(practiceSettingsKey, JSON.stringify(merged));
}

async function getBridgeUrl(): Promise<string> {
  return getSameOriginGatewayUrl();
}

async function setBridgeUrl(_url: string): Promise<void> {
  await setPracticeSettings({ bridgeUrl: getSameOriginGatewayUrl() });
}

async function getKioskEmergencyCache(): Promise<
  KioskEmergencyCacheSnapshot | undefined
> {
  const snapshot = decodeKioskEmergencyCacheJson(
    localStorage.getItem(kioskEmergencyCacheKey),
  );
  if (snapshot === undefined) {
    localStorage.removeItem(kioskEmergencyCacheKey);
  }
  return snapshot;
}

async function setKioskEmergencyCache(
  snapshot: KioskEmergencyCacheSnapshot,
): Promise<void> {
  localStorage.setItem(
    kioskEmergencyCacheKey,
    JSON.stringify(encodeKioskEmergencyCacheSnapshot(snapshot)),
  );
}

async function clearKioskEmergencyCache(): Promise<void> {
  localStorage.removeItem(kioskEmergencyCacheKey);
}

async function getStylingSettings(): Promise<StylingSettings> {
  const stored = readStoredRecord(stylingSettingsKey);
  const merged = {
    ...DEFAULT_STYLING_SETTINGS,
    ...stored,
  };
  const storedPin = typeof stored["pin"] === "string" ? stored["pin"] : "";
  // Same rule as isPinConfigured: a hash counts, a (trimmed) default never does.
  const hasPinConfigured =
    storedPin !== "" && (isHashedPin(storedPin) || !isDefaultPin(storedPin));
  return {
    ...merged,
    pin: hasPinConfigured ? placeholderPin : "",
  };
}

async function setStylingSettings(
  settings: Partial<StylingSettings>,
): Promise<void> {
  const current = readStoredRecord(stylingSettingsKey);
  const next: Record<string, unknown> = {
    ...DEFAULT_STYLING_SETTINGS,
    ...current,
    ...settings,
  };

  if (
    typeof settings.pin === "string" &&
    settings.pin &&
    settings.pin !== placeholderPin
  ) {
    next["pin"] = serializePinHash(await hashPin(settings.pin));
  } else if (settings.pin === placeholderPin || settings.pin === "") {
    next["pin"] = current["pin"] ?? DEFAULT_STYLING_SETTINGS.pin;
  }

  localStorage.setItem(stylingSettingsKey, JSON.stringify(next));
}

function readStoredPin(): string {
  const stored = readStoredRecord(stylingSettingsKey);
  return typeof stored["pin"] === "string" ? stored["pin"] : "";
}

async function isPinConfigured(): Promise<boolean> {
  const pin = readStoredPin();
  if (!pin) return false;
  // A stored default PIN is not a configured secret.
  return isHashedPin(pin) ? true : !isDefaultPin(pin);
}

async function verifyPin(input: string): Promise<boolean> {
  // The forbidden default never unlocks, whatever is stored. A device without
  // a configured PIN is routed into forced setup by the caller instead.
  if (isDefaultPin(input)) return false;

  const stored = readStoredRecord(stylingSettingsKey);
  const pin = typeof stored["pin"] === "string" ? stored["pin"] : "";

  if (!pin) return false;

  if (isHashedPin(pin)) {
    const hash = deserializePinHash(pin);
    return hash ? verifyPinHash(input, hash) : false;
  }

  // Legacy plaintext PIN: migrate on first successful use, but never bless the
  // default value into a hash.
  if (pin === input && !isDefaultPin(pin)) {
    stored["pin"] = serializePinHash(await hashPin(input));
    localStorage.setItem(stylingSettingsKey, JSON.stringify(stored));
    return true;
  }

  return false;
}

function stripSecrets(settings: PracticeSettings): PracticeSettings {
  return {
    ...settings,
    solToken: undefined,
  };
}

function readStoredRecord(key: string): Record<string, unknown> {
  const raw = localStorage.getItem(key);
  if (!raw) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
